import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Settings } from '@mui/icons-material';
import { ListItemIcon } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import { selectRmCreateRouteModal } from 'state/routeMonitoringSlice';
import {
  selectCurrentProjectInfo,
  selectWorkflow,
  setWorkflow,
} from 'state/workflowSlice';

import { WORKFLOWS_ALL_ENABLED, WorkflowType } from 'appConstants';

import { selectUserState } from '../../state/userSlice';
import { ReactComponent as NavClose } from '../common/icons/navClose.svg';
import { ReactComponent as NavOpen } from '../common/icons/navOpen.svg';
import { BetaChip } from './BetaChip';

import styles from './NavBar.module.css';

// user visible names
export const WORKFLOW_PLAN_NAMES = {
  [WorkflowType.Timeline]: 'Timeline',
  [WorkflowType.Plan]: 'Volumes',
  [WorkflowType.Speed]: 'Speeds',
  [WorkflowType.TravelTime]: 'Travel Time',
  [WorkflowType.Detouring]: 'Closure Impacts',
  [WorkflowType.Queuing]: 'Queuing',
  [WorkflowType.RouteMonitoring]: 'Route Monitoring',
  [WorkflowType.OriginDestination]: 'Origin Destination',
};

export function NavBar() {
  const workflow = useAppSelector(selectWorkflow);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const userInfo = useAppSelector(selectUserState);
  const rmModalOpen = useAppSelector(selectRmCreateRouteModal);
  const dispatch = useAppDispatch();
  const disableDisabledFeatures = !userInfo.has_hidden_features;
  const [menuOpen, setMenuOpen] = useState(true);
  const clickCatcherRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (rmModalOpen) setMenuOpen(false);
  }, [rmModalOpen]);

  useEffect(() => {
    if (menuOpen && clickCatcherRef.current) {
      clickCatcherRef.current.focus();
    }
  }, [menuOpen]);

  //  Prevents unnecessary re-sorting every render
  const enabledWorkflows = useMemo(() => {
    let filtered = userProject?.features
      ? WORKFLOWS_ALL_ENABLED.filter((value) =>
          userProject.features.includes(value),
        )
      : [];

    if (!disableDisabledFeatures) filtered = WORKFLOWS_ALL_ENABLED;

    return filtered.sort((a, b) => {
      const a_enabled = userProject?.features.includes(a);
      const b_enabled = userProject?.features.includes(b);
      if ((a_enabled && b_enabled) || (!a_enabled && !b_enabled)) return 0;
      else if (a_enabled && !b_enabled) return -1;
      else return 1;
    });
  }, [userProject, disableDisabledFeatures]);

  useEffect(() => {
    if (!enabledWorkflows.includes(workflow)) {
      dispatch(setWorkflow(enabledWorkflows[0]));
    }
  }, [workflow, userProject]);

  const getWorkflowTitle = (workflowName: WorkflowType) => {
    const title = WORKFLOW_PLAN_NAMES[workflowName];
    if (
      userInfo.has_hidden_features &&
      workflowName == WorkflowType.Detouring
    ) {
      return (
        <>
          {title}&nbsp;
          <BetaChip />
        </>
      );
    }
    return title;
  };

  const menu = (
    <>
      <List disablePadding>
        {enabledWorkflows.map((workflow_name, index) => {
          const isIncludedWorkflow =
            userProject?.features.includes(workflow_name);
          const unchosenClass = isIncludedWorkflow
            ? styles.nav_button
            : styles.nav_button_disabled;
          return (
            <ListItem
              key={workflow_name}
              disablePadding
              className={
                workflow === workflow_name
                  ? styles.nav_button_chosen
                  : unchosenClass
              }
            >
              <ListItemButton
                selected={workflow === workflow_name}
                onClick={(e) => {
                  e.currentTarget.blur();
                  dispatch(setWorkflow(workflow_name));
                  setMenuOpen(false);
                }}
                className={styles.list_item_button}
              >
                <ListItemText
                  primary={getWorkflowTitle(workflow_name)}
                  sx={{
                    '& span': {
                      alignItems: 'center',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      {userInfo.has_sub_admin && (
        <>
          <div className={styles.manage_button_spacer} />
          <ListItem className={styles.manage_button}>
            <ListItemButton
              className={styles.list_item_button}
              onClick={(e) => {
                navigate(
                  `/manage/projects/${userProject.subscription_id}/${userProject.project_slug}/`,
                );
              }}
            >
              <ListItemIcon className={styles.list_item_icon_tight}>
                <Settings />
              </ListItemIcon>
              <ListItemText primary="Manage" />
            </ListItemButton>
          </ListItem>
        </>
      )}
    </>
  );

  const getMaybeHamburgerStyles = () => {
    const alwaysUseHamburgerNav = false;
    if (alwaysUseHamburgerNav) {
      return {
        nav_perm_style: styles.hamburger_nav_bar_permanent,
        click_style: styles.hamburger_mobile_nav_click_catcher,
        menu_style: styles.hamburger_mobile_nav_container_menu,
        button_style: styles.hamburger_mobile_nav_container_button,
      };
    }

    return {
      nav_perm_style: styles.nav_bar_permanent,
      click_style: styles.mobile_nav_click_catcher,
      menu_style: styles.mobile_nav_container_menu,
      button_style: styles.mobile_nav_container_button,
    };
  };

  const maybeHamburgerStyles = getMaybeHamburgerStyles();

  return (
    <>
      <IconButton
        className={maybeHamburgerStyles.button_style}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        {menuOpen ? <NavOpen /> : <NavClose />}
      </IconButton>
      <div className={maybeHamburgerStyles.nav_perm_style}>{menu}</div>
      <div
        className={maybeHamburgerStyles.menu_style}
        style={menuOpen ? { left: 0 } : {}}
      >
        {menuOpen && menu}
      </div>
      {menuOpen && (
        <div
          role="button"
          aria-label="Close menu"
          tabIndex={0}
          ref={clickCatcherRef}
          className={maybeHamburgerStyles.click_style}
          onClick={() => {
            setMenuOpen(false);
          }}
          onKeyUp={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setMenuOpen(false);
            }
          }}
        />
      )}
    </>
  );
}
